import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  isSupportedLocale,
  type SupportedLocale
} from './config';

const readFromStorage = (): SupportedLocale | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && isSupportedLocale(stored)) {
      return stored;
    }
  } catch {
    // localStorage may throw in privacy mode; fall through to next strategy
  }
  return null;
};

const readFromNavigator = (): SupportedLocale | null => {
  if (typeof navigator === 'undefined') {
    return null;
  }
  const candidate = navigator.language?.split('-')[0];
  return candidate && isSupportedLocale(candidate) ? candidate : null;
};

// Reads a forced locale from the `?lang=` query param (e.g. QR-code marketing
// links like `/?lang=es`). Wins over stored/navigator preferences.
export const readQueryLocale = (): SupportedLocale | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const value = new URLSearchParams(window.location.search).get('lang');
    return value && isSupportedLocale(value) ? value : null;
  } catch {
    // malformed URL / unavailable; fall through to next strategy
  }
  return null;
};

export const detectLanguage = (): SupportedLocale =>
  readQueryLocale() ??
  readFromStorage() ??
  readFromNavigator() ??
  DEFAULT_LOCALE;

export const persistLanguage = (locale: SupportedLocale): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // localStorage unavailable; in-memory selection still works for the session
  }
};
